import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Download, ShieldAlert, ShieldCheck, Trash2, UploadCloud, ExternalLink, Users2 } from "lucide-react";
import { CloudIcon, LangIcon, ProviderIcon } from "@/components/brand-icons";
import { PushToGitDialog } from "@/components/push-to-git";
import { ShareDialog } from "@/components/share-dialog";
import { PushHistory } from "@/components/push-history";
import { FileExplorer } from "@/components/file-explorer";
import { ProjectCost } from "@/components/project-cost";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(null);
  const [pushOpen, setPushOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    client.get(`/projects/${id}`).then((r) => {
      setP(r.data);
      setActive(r.data.files?.find((f) => f.path.endsWith("main.tf") || f.path.endsWith("main.bicep"))?.path || r.data.files?.[0]?.path);
    });
  }, [id]);

  useEffect(() => {
    if (params.get("push") === "1") {
      setPushOpen(true);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const download = async () => {
    try {
      const res = await client.get(`/projects/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${p.name}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this project?")) return;
    await client.delete(`/projects/${id}`);
    toast.success("Project deleted");
    nav("/app/projects");
  };

  if (!p) return <div className="p-10 text-sm text-slate-500">Loading project…</div>;
  const canManage = p.can_manage ?? (user?.role === "admin" || (p.owner_id && p.owner_id === user?.id));
  const canPush = p.can_push ?? canManage;
  const failed = (p.compliance?.checks || []).filter((c) => c.status !== "pass").length;

  return (
    <div className="p-10">
      <Link to="/app/projects" className="mb-6 inline-flex items-center text-sm text-slate-500 hover:text-slate-900" data-testid="back-projects">
        <ArrowLeft className="mr-1 h-4 w-4" /> All projects
      </Link>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">// project</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight" data-testid="project-name">{p.name}</h1>
          <p className="mt-1 text-sm text-slate-600">{p.description || "No description"}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Badge className="flex items-center gap-1.5 rounded-full bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">
              <CloudIcon cloud={p.cloud} className="h-3 w-3" /> {p.cloud}
            </Badge>
            <Badge className="flex items-center gap-1.5 rounded-full bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">
              <LangIcon language={p.language} className="h-3 w-3" /> {p.language}
            </Badge>
            <Badge className="rounded-full bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">{p.region}</Badge>
            {p.last_push && (
              <a
                href={p.last_push.tree_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest text-emerald-700 hover:bg-emerald-100"
                data-testid="last-push-badge"
              >
                <ProviderIcon provider={p.last_push.provider} className="h-3 w-3" /> {p.last_push.repo} @ {p.last_push.branch}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {canManage && (
            <Button variant="outline" onClick={remove} data-testid="delete-project" className="h-10 border-slate-200 text-slate-700 hover:bg-slate-50">
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          )}
          {canManage && (
            <Button variant="outline" onClick={() => setShareOpen(true)} data-testid="share-project" className="h-10 border-slate-200 text-slate-700 hover:bg-slate-50">
              <Users2 className="mr-2 h-4 w-4" /> Share
              {p.collaborators?.length > 0 && <span className="ml-2 rounded-full bg-slate-100 px-1.5 font-mono-tight text-[10px] text-slate-600" data-testid="collaborator-count">{p.collaborators.length}</span>}
            </Button>
          )}
          {canPush && (
            <Button variant="outline" onClick={() => setPushOpen(true)} data-testid="push-to-git" className="h-10 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white">
              <UploadCloud className="mr-2 h-4 w-4" /> Push to Git
            </Button>
          )}
          <Button onClick={download} data-testid="download-zip" className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800">
            <Download className="mr-2 h-4 w-4" /> Download ZIP
          </Button>
        </div>
      </div>

      <PushToGitDialog open={pushOpen} onOpenChange={setPushOpen} project={p} onPushed={(r) => setP((x) => ({ ...x, last_push: r, pushes: [r, ...(x.pushes || [])] }))} />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} project={p} onChanged={(list) => setP((x) => ({ ...x, collaborators: list }))} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 inline-flex border border-slate-200 bg-white" data-testid="project-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">Overview</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            Compliance
            <span className={cn("ml-2 rounded-full px-1.5 font-mono-tight text-[10px]", p.compliance?.compliant ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")} data-testid="compliance-score-badge">{p.compliance?.score}%</span>
          </TabsTrigger>
          <TabsTrigger value="cost" data-testid="tab-cost" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            Estimated cost
            {p.budget_alerts?.length > 0 && <span className="ml-2 rounded-full bg-rose-50 px-1.5 font-mono-tight text-[10px] text-rose-700" data-testid="budget-alert-badge">over budget</span>}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            Push history
            <span className="ml-2 rounded-full bg-slate-100 px-1.5 font-mono-tight text-[10px] text-slate-600 data-[state=active]:bg-white" data-testid="push-count">{p.pushes?.length || 0}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="history">
          <PushHistory pushes={p.pushes || []} />
        </TabsContent>

        <TabsContent value="overview">
          <FileExplorer files={p.files || []} root={p.name} active={active} onSelect={setActive} height="calc(100vh - 7rem)" />
        </TabsContent>

        <TabsContent value="compliance">
          <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none" data-testid="compliance-panel">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h2 className="font-display text-lg font-semibold">Compliance</h2>
                <p className="mt-1 text-sm text-slate-500">{failed ? `${failed} check${failed > 1 ? "s" : ""} failing against the current governance rules.` : "Every governance check passes."}</p>
              </div>
              <span className={cn("inline-flex items-center gap-1.5 font-display text-2xl font-semibold", p.compliance?.compliant ? "text-emerald-600" : "text-amber-600")} data-testid="compliance-score">
                {p.compliance?.compliant ? <ShieldCheck className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
                {p.compliance?.score}%
              </span>
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {p.compliance?.checks?.map((c) => (
                <div key={c.id} className={cn("rounded-sm border px-3 py-2 text-sm", c.status === "pass" ? "border-slate-100 bg-slate-50/60" : "border-amber-200 bg-amber-50/60")} data-testid={`check-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-800">{c.label}</span>
                    <span className={cn("font-mono-tight text-[10px] uppercase tracking-widest", c.status === "pass" ? "text-emerald-600" : "text-amber-600")}>{c.status}</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{c.detail}</p>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="cost">
          <ProjectCost projectId={p.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
